import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client'
import { AccountRow } from './AccountRow'
import { Sheet } from './Sheet'

/*
  Closing an account.

  Its own component rather than a lump of Settings, because it has to be reachable from two
  places. Settings is the obvious one. The other is the wall: an account whose subscription
  has lapsed is exactly the account somebody wants to close, and if the only route to the
  button is behind a screen they can no longer open then the button does not really exist.
  App Store Review Guideline 5.1.1(v) is not satisfied by a delete you can only reach while
  you are paying, and /api/account is exempt from the paywall for the same reason.
*/

export interface Impact {
  company: string
  isOwner: boolean
  otherPeople: number
  meetings: number
  documents: number
  commitments: number
}

/** Reads what deleting would destroy. Null while loading, and on any failure. */
export function useDeletionImpact() {
  const [impact, setImpact] = useState<Impact | null>(null)

  useEffect(() => {
    api.get<Impact>('/api/account/deletion').then(setImpact).catch(() => setImpact(null))
  }, [])

  return impact
}

/** The Settings card. */
export function CloseAccountCard() {
  const impact = useDeletionImpact()
  const [open, setOpen] = useState(false)

  if (!impact) return null

  return (
    <div className="card card--rows">
      <div className="card__title">Closing your account</div>
      <AccountRow
        label={impact.isOwner ? `Delete ${impact.company}` : 'Leave this board'}
        note={impact.isOwner
          ? 'Ends the board and deletes everything on it, for everyone. It cannot be undone.'
          : 'Removes you from this board. What you put on the table stays with the business.'}
        action={
          <button className="btn btn--small btn--danger" onClick={() => setOpen(true)}>
            {impact.isOwner ? 'Delete' : 'Leave'}
          </button>
        }
      />
      {open && <ConfirmClose impact={impact} onClose={() => setOpen(false)} />}
    </div>
  )
}

/**
 * The same thing as one quiet line, for the wall.
 *
 * Deliberately not a button competing with Sign out. Somebody who has landed on the wall is
 * usually there to sort their billing out, not to delete four years of board minutes.
 */
export function CloseAccountLink() {
  const impact = useDeletionImpact()
  const [open, setOpen] = useState(false)

  if (!impact) return null

  return (
    <>
      <button type="button" className="linkish" onClick={() => setOpen(true)}>
        {impact.isOwner ? 'Delete this account' : 'Leave this board'}
      </button>
      {open && <ConfirmClose impact={impact} onClose={() => setOpen(false)} />}
    </>
  )
}

function ConfirmClose({ impact, onClose }: { impact: Impact; onClose: () => void }) {
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ready = !impact.isOwner
    || typed.trim().toLowerCase() === impact.company.trim().toLowerCase()

  const go = useCallback(async () => {
    if (busy || !ready) return
    setBusy(true)
    setError(null)
    try {
      await api.del<void>('/api/account', { confirm: typed })
      /*
        A hard navigation, not a state change. The session cookie is gone and every screen
        behind this one is about to 401 on its next poll, so reloading is the only way to be
        sure nothing is left holding data from an account that no longer exists.
      */
      window.location.href = '/'
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not close the account')
      setBusy(false)
    }
  }, [busy, ready, typed])

  return (
    <Sheet title={impact.isOwner ? 'Delete this account' : 'Leave this board'} onClose={onClose}>
      {impact.isOwner ? (
        <>
          <p className="muted" style={{ marginBottom: 'var(--space-3)' }}>
            This deletes <strong>{impact.company}</strong> and everything on its board. It is
            immediate and there is no copy kept.
          </p>

          <ul className="muted" style={{ margin: '0 0 var(--space-4)', paddingLeft: '1.2em' }}>
            <li>{count(impact.meetings, 'meeting')}, with every position and vote in them</li>
            <li>{count(impact.documents, 'document')} and everything read out of them</li>
            <li>{count(impact.commitments, 'commitment')}</li>
            {impact.otherPeople > 0 && (
              <li>
                {count(impact.otherPeople, 'other person', 'other people')} signed in to this
                board, who lose access without being asked
              </li>
            )}
          </ul>

          <label className="field">
            <span className="field__label">
              Type <strong>{impact.company}</strong> to confirm
            </span>
            <input className="input" value={typed} autoFocus
                   onChange={e => setTyped(e.target.value)} />
          </label>
        </>
      ) : (
        <p className="muted" style={{ marginBottom: 'var(--space-4)' }}>
          You will be signed out and removed from {impact.company}. What you put on the table
          stays with the business, because it is the board's record of what was discussed
          rather than yours.
        </p>
      )}

      {error && <div className="signin__error">{error}</div>}

      <div className="sheet__actions">
        <button type="button" className="btn" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn--danger" disabled={busy || !ready}
                onClick={() => void go()}>
          {busy ? 'Closing' : impact.isOwner ? 'Delete everything' : 'Leave this board'}
        </button>
      </div>
    </Sheet>
  )
}

/** "3 meetings", "One document", "No commitments". Zero reads better as a word. */
function count(n: number, one: string, many?: string): string {
  const plural = many ?? `${one}s`
  if (n === 0) return `No ${plural}`
  if (n === 1) return `One ${one}`
  return `${n} ${plural}`
}
